import { CartItem, CartItemGroup, PackageVersionStatus } from './cart.model';
import { PublicBusiness } from '../../../../../../api/src/business/business.entity';
import { CreativeUnitPackageDto } from '../../../../../../api/src/creative-unit-package/models/creative-unit-package.dto';
import { CreativePackageUtils } from '../../../../../../api/src/creative-unit-package/utils/creative-package.utils';
import { CreativeUnitUtils } from '../../../../../../api/src/creative-unit-package/utils/creative-unit.utils';
import {
	CustomizationItemDto,
	CustomizationSelectDto,
	CustomizationType
} from '../../../../../../api/src/creative-unit-package/models/customization/customization.dto';
import { CustomizationOptionDto } from '../../../../../../api/src/creative-unit-package/models/customization/customization-option.dto';
import { CustomizationUtils } from '../../../../../../api/src/creative-unit-package/utils/customization.utils';
import { CustomizationOptionUtils } from '../../../../../../api/src/creative-unit-package/utils/customization-option.utils';
import { MergePropertiesUtils } from '../../../../../../api/src/creative-unit-package/utils/merge-properties.utils';
import {
	CreativeUnitDto,
	CreativeUnitWithCustomizationsDto
} from '../../../../../../api/src/creative-unit-package/models/creative-unit/creative-unit.dto';
import { LayerDto } from '../../../../../../api/src/creative-unit-package/models/layer/layer.dto';
import { LayersUtils } from '../../../../../../api/src/creative-unit-package/utils/layers.utils';
import { CacheBusterUtils } from '../../../../../../api/src/_core/utils/utils.cache-buster';
import { CreativeListingUtils } from '../../../../../../api/src/creative-unit-package/utils/creative-listing.utils';
import { MergeTags } from '../../../../../../api/src/_core/utils/utils.merge-tags';
import { isEqual } from 'lodash';

/** Every descendant, matching how `LayersUtils.mapAllLayers` walks — not just GROUP children. */
function allLayersDeep(layers: LayerDto[]): LayerDto[] {
	return LayersUtils.findAllLayers(layers || [], () => true);
}

export type ApplyDynamicDefaultValuesFn = (
	customizations: CustomizationItemDto[],
	pkg: CreativeUnitPackageDto,
	business?: PublicBusiness,
	unit?: CreativeUnitDto,
	activeGroup?: CartItemGroup,
	onlyApplyDownstreamOfId?: string
) => Promise<CustomizationItemDto[]>;

export interface BuildCartItemsForInPlaceUpgradeOptions {
	activeGroup?: CartItemGroup;
	applyDynamicDefaultValues?: ApplyDynamicDefaultValuesFn;
	/** Version the items are currently on. Builds the layer baseline; without it nothing is carried. */
	currentPackage?: CreativeUnitPackageDto;
}

export interface CartPackageUpgradeChangeSummary {
	addedCustomizations: string[];
	removedCustomizations: string[];
	addedUnits: string[];
	skippedUnits: string[];
	addedLayers: string[];
	removedLayers: string[];
	changedLayers: string[];
	hasChanges: boolean;
}

/**
 * The single rule for whether a cart item is on the newest version of its template.
 * The API sends `latestPublishedVersionId` as the newest published version created after the item's
 * package, or the package itself when nothing newer is published — so a draft-only template is
 * `latest`. It is missing only when the package row is gone or deleted: `unknown`, no claim either
 * way. `needsUpgrade` therefore always comes with a real, newer, published target id.
 */
export function resolvePackageVersionStatus(item: CartItem | undefined): PackageVersionStatus {
	const latest = item?.metadata?.latestPublishedVersionId as string | undefined;
	const current = item?.metadata?.packageId ?? item?.itemData?.packageId;
	if (!latest || !current) {
		return 'unknown';
	}
	return latest === current ? 'latest' : 'needsUpgrade';
}

function customizationDisplayLabel(customization: CustomizationItemDto): string {
	return customization.label || customization.id;
}

function unitDisplayLabel(unit: CreativeUnitDto): string {
	return unit.name || unit.id;
}

function cartItemUnitLabel(item: CartItem): string {
	return item.name || item.itemData?.name || item.metadata?.listingName || item.id;
}

function layerDisplayLabel(layer: LayerDto): string {
	return layer.label || layer.id;
}

function customizationLabelMap(packageDto: CreativeUnitPackageDto): Map<string, string> {
	const map = new Map<string, string>();
	for (const customization of CustomizationUtils.flattenCustomizations(packageDto.customizations || [])) {
		if (!customization?.id || customization.type === CustomizationType.GROUP) {
			continue;
		}
		map.set(customization.id, customizationDisplayLabel(customization));
	}
	return map;
}

/**
 * Rebuilds a cart item's unit the same way that item was created. Listing items must go through
 * `buildUnitFromListing`: it applies the listing's mappings to layers and customizations, and the raw
 * package unit has none of them, so those mappings would read as user edits in the 3-way carry.
 *
 * The listing result is layered *over* the package unit rather than replacing it. `buildUnitFromListing`
 * assembles its own override bag and never sets some fields `createCreativeUnitWithCustomizations`
 * provides (`background` is the one that bites); keys it omits therefore fall through to the package
 * unit instead of being dropped from the upgraded item.
 */
function buildUnitForCartItem(
	packageDto: CreativeUnitPackageDto,
	unitId: string,
	source: CartItem,
	business?: PublicBusiness,
	activeGroup?: CartItemGroup
): CreativeUnitWithCustomizationsDto | undefined {
	const unit = packageDto.creativeUnits?.find(u => u.id === unitId);
	const packageUnit = unit ? CreativePackageUtils.createCreativeUnitWithCustomizations(packageDto, unit, business) : undefined;

	const listingId = source.metadata?.listingId;
	const listing = listingId ? packageDto.creativeUnitListingSettings?.listings?.find(l => l.id === listingId) : undefined;

	if (listing) {
		try {
			const listingUnit = CreativeListingUtils.buildUnitFromListing(listing, packageDto, {
				specificCreativeUnitId: unitId,
				business,
				activeGroup: activeGroup as any
			});
			return packageUnit ? { ...packageUnit, ...listingUnit } : listingUnit;
		} catch (e) {
			// A hidden listing throws — fall through rather than failing the whole upgrade.
			console.warn('unable to rebuild cart item from listing, falling back to the package unit', listingId, e);
		}
	}

	return packageUnit;
}

/**
 * Resolves the package's own customization defaults into the unit's layers. Neither builder does this,
 * but a saved cart item's layers always have values applied — without it every customization-driven
 * style reads as a user edit.
 */
function withResolvedDefaults(unit: CreativeUnitWithCustomizationsDto): CreativeUnitWithCustomizationsDto {
	// Same precedence as `isUserEditedCustomization`, so both sides of the 3-way agree on "default".
	const resolved = CustomizationUtils.flattenCustomizations(unit.customizations || []).map(customization => ({
		...customization,
		value: customization.value ?? (customization as CustomizationSelectDto).defaultOption ?? customization.defaultValue
	})) as CustomizationItemDto[];

	return CreativeUnitUtils.updateCreativeUnitWithCustomizationsValues(
		{ ...unit, customizations: resolved },
		resolved
	) as CreativeUnitWithCustomizationsDto;
}

function mergedUnitLayers(packageDto: CreativeUnitPackageDto, unit: CreativeUnitDto, business?: PublicBusiness): LayerDto[] {
	const built = CreativePackageUtils.createCreativeUnitWithCustomizations(packageDto, unit, business);
	return allLayersDeep(withResolvedDefaults(built).layers || []);
}

function layerContentSignature(layer: LayerDto): string {
	const { id, layers, ...content } = layer;
	return JSON.stringify(content);
}

function compareLayerSets(
	oldLayers: LayerDto[],
	newLayers: LayerDto[],
	addedLayers: Set<string>,
	removedLayers: Set<string>,
	changedLayers: Set<string>
): void {
	const oldById = new Map(oldLayers.map(layer => [layer.id, layer]));
	const newById = new Map(newLayers.map(layer => [layer.id, layer]));

	for (const [id, layer] of oldById) {
		if (!newById.has(id)) {
			removedLayers.add(layerDisplayLabel(layer));
		}
	}

	for (const [id, layer] of newById) {
		if (!oldById.has(id)) {
			addedLayers.add(layerDisplayLabel(layer));
			continue;
		}

		const oldLayer = oldById.get(id);
		if (oldLayer && layerContentSignature(oldLayer) !== layerContentSignature(layer)) {
			changedLayers.add(layerDisplayLabel(layer));
		}
	}
}

/**
 * Compares the current package/cart state with a target package version for the upgrade dialog.
 */
export function comparePackageUpgradeChanges(
	cartItems: CartItem[],
	currentPackage: CreativeUnitPackageDto,
	targetPackage: CreativeUnitPackageDto,
	fullItemsById?: Map<string, CartItem>,
	business?: PublicBusiness,
	versions?: CreativeUnitPackageDto[]
): CartPackageUpgradeChangeSummary {
	const addedCustomizations = new Set<string>();
	const removedCustomizations = new Set<string>();
	const addedUnits = new Set<string>();
	const skippedUnits = new Set<string>();
	const addedLayers = new Set<string>();
	const removedLayers = new Set<string>();
	const changedLayers = new Set<string>();

	const oldCustomizationLabels = customizationLabelMap(currentPackage);
	const newCustomizationLabels = customizationLabelMap(targetPackage);

	for (const [id, label] of oldCustomizationLabels) {
		if (!newCustomizationLabels.has(id)) {
			removedCustomizations.add(label);
		}
	}
	for (const [id, label] of newCustomizationLabels) {
		if (!oldCustomizationLabels.has(id)) {
			addedCustomizations.add(label);
		}
	}

	const oldUnits = currentPackage.creativeUnits || [];
	const newUnits = targetPackage.creativeUnits || [];
	const oldUnitIds = new Set(oldUnits.map(unit => unit.id));
	const newUnitIds = new Set(newUnits.map(unit => unit.id));

	for (const unit of newUnits) {
		if (!oldUnitIds.has(unit.id)) {
			addedUnits.add(unitDisplayLabel(unit));
		}
	}

	for (const item of cartItems) {
		const source = fullItemsById?.get(item.id) ?? item;
		const unitId = resolveCreativeUnitIdForTargetPackage(source, targetPackage);
		const targetUnit = unitId ? targetPackage.creativeUnits?.find(unit => unit.id === unitId) : undefined;
		if (!unitId || !targetUnit) {
			skippedUnits.add(cartItemUnitLabel(source));
		}
	}

	for (const oldUnit of oldUnits) {
		if (!newUnitIds.has(oldUnit.id)) {
			continue;
		}
		const newUnit = newUnits.find(unit => unit.id === oldUnit.id);
		if (!newUnit) {
			continue;
		}
		compareLayerSets(
			mergedUnitLayers(currentPackage, oldUnit, business),
			mergedUnitLayers(targetPackage, newUnit, business),
			addedLayers,
			removedLayers,
			changedLayers
		);
	}

	const summary: CartPackageUpgradeChangeSummary = {
		addedCustomizations: [...addedCustomizations],
		removedCustomizations: [...removedCustomizations],
		addedUnits: [...addedUnits],
		skippedUnits: [...skippedUnits],
		addedLayers: [...addedLayers],
		removedLayers: [...removedLayers],
		changedLayers: [...changedLayers],
		hasChanges: false
	};

	summary.hasChanges =
		summary.addedCustomizations.length > 0 ||
		summary.removedCustomizations.length > 0 ||
		summary.addedUnits.length > 0 ||
		summary.skippedUnits.length > 0 ||
		summary.addedLayers.length > 0 ||
		summary.removedLayers.length > 0 ||
		summary.changedLayers.length > 0;

	return summary;
}

/**
 * Picks the first valid option from the target package when a carried value no longer exists.
 */
function resolveFallbackOptionForTargetCustomization(
	targetCustomization: CustomizationItemDto,
	mergeTagState?: Record<string, unknown>
): CustomizationOptionDto | undefined {
	const options = (targetCustomization as CustomizationSelectDto).options || [];
	if (!options.length) {
		return undefined;
	}

	const visibleOptions = mergeTagState ? CustomizationOptionUtils.getVisibleOptions(options, mergeTagState) : options;
	const candidates = visibleOptions.length ? visibleOptions : options;

	const defaultCandidate = targetCustomization.defaultOption ?? targetCustomization.defaultValue;
	if (defaultCandidate && typeof defaultCandidate === 'object') {
		const matchedDefault = candidates.find(option =>
			CustomizationOptionUtils.areOptionsEqual(option, defaultCandidate as CustomizationOptionDto, false)
		);
		if (matchedDefault) {
			return matchedDefault;
		}
	}

	return candidates[0];
}

/**
 * Resolves a carried customization value against the target package definition.
 * When an old option was removed from the new package, a valid target option is chosen instead.
 */
export function resolveCarriedCustomizationValue(
	targetCustomization: CustomizationItemDto,
	oldCustomization: CustomizationItemDto,
	mergeTagState?: Record<string, unknown>
): unknown | undefined {
	const oldValue = oldCustomization.value;
	if (oldValue == null) {
		return undefined;
	}

	if (targetCustomization.type !== CustomizationType.SELECT) {
		return oldValue;
	}

	const carriedOption = oldValue as CustomizationOptionDto;
	const selectCustomization = targetCustomization as CustomizationSelectDto;
	const targetOptions = targetCustomization.options;

	if (targetOptions?.length === 0) {
		return oldValue;
	}
	const matched =
		CustomizationSelectDto.findMatchedValue(selectCustomization, carriedOption) ||
		targetOptions?.find(option => CustomizationOptionUtils.areOptionsEqual(option, carriedOption, false));

	if (matched) {
		return matched;
	}

	const looseMatches = CustomizationSelectDto.findMatchedValues(selectCustomization, carriedOption, undefined, false);
	if (looseMatches.length) {
		return looseMatches[0];
	}

	// A dynamic customization's stored options are not the list the user chose from — they are a
	// snapshot, and for one that became dynamic after authoring they are stale entries from a
	// different generation entirely. Failing to find the value among them proves nothing, so hand it
	// back untouched: `getDefaultValue` re-checks it against the options actually fetched and drops it
	// there if it has really gone. Substituting here instead replaces a valid selection with a default
	// and, because a substitution counts as a change, clears the customizations downstream of it too.
	if (selectCustomization.dynamicLink) {
		return oldValue;
	}

	if (oldCustomization.dirty !== false) {
		return resolveFallbackOptionForTargetCustomization(targetCustomization, mergeTagState);
	}

	return undefined;
}

/** The comparable scalar behind a customization value — an option collapses to what it resolves to. */
function customizationValueSignature(value: unknown): unknown {
	if (value && typeof value === 'object' && 'value' in value) {
		return (value as CustomizationOptionDto).value;
	}
	return value;
}

/**
 * Loose equality for customization values. The same colour arrives in different shapes — a package
 * default can be a bare `'#cd2727'` where the saved value is a full option object — so compare what
 * they resolve to, not how they are wrapped.
 */
function isSameCustomizationValue(a: unknown, b: unknown): boolean {
	return isEqual(a, b) || isEqual(customizationValueSignature(a), customizationValueSignature(b));
}

/**
 * Whether a saved value is a genuine user edit rather than a materialized default.
 *
 * The 3-way compare wins whenever a baseline exists, like `carryLayerEditsForUpgrade`. `dirty` is
 * deliberately not the primary signal — the start-up wizard, variation generation and the API's
 * apply-values path all set it without the user touching anything. `dirty` only decides when the old
 * package cannot describe the customization at all; an absent flag then keeps the value, since
 * nothing disproves an edit.
 */
function isUserEditedCustomization(sourceCustomization: CustomizationItemDto, baselineCustomization?: CustomizationItemDto): boolean {
	const baselineValues = baselineCustomization
		? [
				baselineCustomization.value,
				(baselineCustomization as CustomizationSelectDto).defaultOption,
				baselineCustomization.defaultValue
		  ].filter(value => value != null)
		: [];

	if (baselineValues.length) {
		return !baselineValues.some(baselineValue => isSameCustomizationValue(baselineValue, sourceCustomization.value));
	}

	return sourceCustomization.dirty !== false;
}

/** A value carried onto the target package, and whether resolution had to substitute something else. */
export interface CarriedCustomizationValue {
	customizationId: string;
	value: unknown;
	/** True when the target package could not express the user's choice and another option was chosen. */
	substituted: boolean;
}

/**
 * Carries old values for every target-package customization the cart item edited. Ids only on the old
 * item (removed in the new package) are dropped; ids only on the new package get their default applied
 * downstream.
 *
 * `itemData.customizations` holds a materialized value for everything, touched or not, so "has a value"
 * does not mean "the user set it" — `baselineCustomizations` (the same unit per the *old* package) is
 * what tells the two apart.
 */
export function cartItemCustomizationsToBatchValuesForTargetPackage(
	customizations: CustomizationItemDto[] | undefined,
	targetPackage: CreativeUnitPackageDto,
	baselineCustomizations?: CustomizationItemDto[]
): CarriedCustomizationValue[] {
	if (!customizations?.length || !targetPackage?.customizations?.length) {
		return [];
	}

	const baselineById = new Map((baselineCustomizations || []).filter(c => c?.id).map(c => [c.id, c]));

	const oldCustomizationsById = new Map<string, CustomizationItemDto>();
	for (const customization of CustomizationUtils.flattenCustomizations(customizations)) {
		if (!customization?.id || customization.value == null) {
			continue;
		}
		if (!isUserEditedCustomization(customization, baselineById.get(customization.id))) {
			continue;
		}
		oldCustomizationsById.set(customization.id, customization);
	}
	if (!oldCustomizationsById.size) {
		return [];
	}

	const result: CarriedCustomizationValue[] = [];
	for (const targetCustomization of CustomizationUtils.flattenCustomizations(targetPackage.customizations)) {
		if (!targetCustomization?.id || !oldCustomizationsById.has(targetCustomization.id)) {
			continue;
		}

		const oldCustomization = oldCustomizationsById.get(targetCustomization.id);
		const resolvedValue = resolveCarriedCustomizationValue(targetCustomization, oldCustomization, undefined);
		if (resolvedValue === undefined) {
			continue;
		}

		const substituted = !isSameCustomizationValue(oldCustomization.value, resolvedValue);

		result.push({
			customizationId: targetCustomization.id,
			value: resolvedValue,
			// Whether resolution handed back what the user chose. Settled here because this is the only
			// place holding both sides; downstream only sees the resolved value.
			substituted
		});
	}
	return result;
}

/**
 * Applies carried customization values to a freshly created unit, respecting merge-tag
 * state and option visibility rules.
 */

export function applyCarriedCustomizationsToUnit(
	unit: CreativeUnitWithCustomizationsDto,
	targetPackage: CreativeUnitPackageDto,
	carriedValues: CarriedCustomizationValue[],
	business?: PublicBusiness,
	activeGroup?: CartItemGroup
): CreativeUnitWithCustomizationsDto {
	let updatedUnit = unit;
	let mergeTagState = MergePropertiesUtils.getMergeableProperties(
		{ ...targetPackage, customizations: updatedUnit.customizations },
		business,
		updatedUnit,
		activeGroup as any
	);

	for (const { customizationId, value, substituted } of carriedValues) {
		// Only genuine user edits reach here, so dirty is honest. A value that fell back to another
		// option is not the user's choice — it stays non-dirty and keeps tracking the target package.
		// Asking whether resolution substituted anything answers that directly; asking whether the
		// value appears in the target's option list does not, because a fallback is always drawn from
		// that list while a runtime-fetched customization stores no list to be found in.
		const carryAsDirty = !substituted;

		updatedUnit = CreativeUnitUtils.updateCustomizationValue(updatedUnit, customizationId, value, carryAsDirty, mergeTagState);
		updatedUnit = CreativeUnitUtils.updateCreativeUnitWithCustomizationValue(updatedUnit, customizationId, value);

		mergeTagState = MergePropertiesUtils.getMergeableProperties(
			{ ...targetPackage, customizations: updatedUnit.customizations },
			business,
			updatedUnit,
			activeGroup as any
		);
	}

	return updatedUnit;
}

function getSelectCustomizationIdsWithChangedCarriedValues(
	source: CartItem,
	targetPackage: CreativeUnitPackageDto,
	carriedValues: { customizationId: string; value: unknown }[]
): string[] {
	const oldCustomizationsById = new Map<string, CustomizationItemDto>();
	for (const customization of CustomizationUtils.flattenCustomizations(source.itemData?.customizations || [])) {
		if (customization?.id) {
			oldCustomizationsById.set(customization.id, customization);
		}
	}

	const targetCustomizationsById = new Map(
		CustomizationUtils.flattenCustomizations(targetPackage.customizations || []).map(customization => [customization.id, customization])
	);

	const changed: string[] = [];
	for (const { customizationId, value } of carriedValues) {
		const targetCustomization = targetCustomizationsById.get(customizationId);
		if (!targetCustomization || targetCustomization.type !== CustomizationType.SELECT) {
			continue;
		}

		const oldCustomization = oldCustomizationsById.get(customizationId);
		if (!oldCustomization?.value) {
			continue;
		}

		// What the option resolves to, not how it is shaped. The carried value is the *target*
		// package's option object, so a re-rendered merge-tag label or any other presentational
		// difference makes the serialized forms diverge while the selection is the same one. Reading
		// that as a change clears everything downstream of a field the user never touched.
		if (!isSameCustomizationValue(oldCustomization.value, value)) {
			changed.push(customizationId);
		}
	}

	return changed;
}

function clearDownstreamCustomizationValues(
	unit: CreativeUnitWithCustomizationsDto,
	fromCustomizationId: string,
	mergeTagState?: Record<string, unknown>
): CreativeUnitWithCustomizationsDto {
	const flat = CustomizationUtils.flattenCustomizations(unit.customizations || []);
	const fromIndex = flat.findIndex(customization => customization.id === fromCustomizationId);
	if (fromIndex < 0) {
		return unit;
	}

	let updatedUnit = unit;
	for (let i = fromIndex + 1; i < flat.length; i++) {
		updatedUnit = CreativeUnitUtils.updateCustomizationValue(updatedUnit, flat[i].id, undefined, false, mergeTagState);
	}

	return updatedUnit;
}

/**
 * Resolves a creative unit id on `targetPackage` for an existing cart item.
 * Package units are identified by `CreativeUnitDto.id` on `itemData` (see `getCartItemMetadata` → `metadata.id`).
 * `itemData.creativeUnitId` is optional and only set for some clone flows — do not rely on it alone.
 */
export function resolveCreativeUnitIdForTargetPackage(item: CartItem, targetPackage: CreativeUnitPackageDto): string | null {
	const candidates = [item.itemData?.creativeUnitId, item.itemData?.id, item.metadata?.id, item.metadata?.creativeUnitId].filter(
		(value): value is string => typeof value === 'string' && value.trim().length > 0
	);

	for (const candidate of candidates) {
		if (targetPackage.creativeUnits?.some(unit => unit.id === candidate)) {
			return candidate;
		}
	}
	return null;
}

/**
 * The asset a layer actually renders, cache-buster stripped. Mirrors the runtime conversion in
 * `CreativeUnitUtils`, which overwrites `layer[layer.type].assetPath` from a top-level `layer.assetPath`
 * and resolves merge tags first — so two layers on the same `{{ var }}` asset don't read as a swap.
 */
function layerAssetSignature(layer: LayerDto, mergeTagState?: Record<string, unknown>): string | undefined {
	// `layer[layer.type]` is the per-type asset bag (`image`, `video`, …), not indexable on the DTO.
	const typedAsset = layer?.type ? (layer as unknown as Record<string, unknown>)[layer.type] : undefined;
	const typedAssetPath = typedAsset && typeof typedAsset === 'object' ? (typedAsset as { assetPath?: string }).assetPath : undefined;

	const assetPath = layer?.assetPath || typedAssetPath || layer?.image?.assetPath || layer?.video?.assetPath;
	if (!assetPath) {
		return undefined;
	}

	const resolved = mergeTagState ? MergeTags.applyMergeTagsToString(assetPath, mergeTagState) : assetPath;
	return CacheBusterUtils.removeCacheBusterFromUrl(resolved) || undefined;
}

/**
 * True when the layer still resolves to the same asset. Asymmetric, like the divergence check in
 * `CustomizationImagePickerDto.resolveCropResetForValue`.
 */
function isSameResolvedAsset(freshLayer: LayerDto, sourceLayer: LayerDto, mergeTagState?: Record<string, unknown>): boolean {
	const freshAsset = layerAssetSignature(freshLayer, mergeTagState);
	const sourceAsset = layerAssetSignature(sourceLayer, mergeTagState);

	// Nothing establishes what the crop was drawn against.
	if (!sourceAsset) {
		return false;
	}

	// Asset resolved later (e.g. from a customization value), so nothing contradicts the crop yet.
	if (!freshAsset) {
		return true;
	}

	return freshAsset === sourceAsset;
}

/** `LayerDto.style` is `any`; this keeps the merge boundary checked. */
type StyleBag = Record<string, unknown> | null | undefined;

/**
 * Overlays the sub-keys that differ from the baseline onto the fresh object. Source and baseline keys
 * are unioned, so a key the user removed is cleared too.
 */
function carryChangedKeys(freshValue: StyleBag, sourceValue: StyleBag, baselineValue: StyleBag): StyleBag {
	if (!sourceValue || typeof sourceValue !== 'object') {
		return isEqual(sourceValue, baselineValue) ? freshValue : sourceValue;
	}

	let result = freshValue;
	const keys = new Set([...Object.keys(sourceValue), ...Object.keys(baselineValue || {})]);

	for (const key of keys) {
		if (isEqual(sourceValue[key], baselineValue?.[key])) {
			continue;
		}
		result = { ...(result || {}), [key]: sourceValue[key] };
	}

	return result;
}

/**
 * Carries the user's layer edits onto layers rebuilt from the target package, matched by layer id:
 * `cropData` and a manual `optimizations.targetCrop` whole (each is a self-consistent crop),
 * `style` per sub-key (the canvas handlers re-emit the whole style object, so only per-key isolates
 * the real edit).
 * `itemData.layers` is the merged package+unit set, so "present on the cart item" does not mean "the
 * user set it" — hence the 3-way rule against `baselineLayers` (the same unit per the *old* package).
 * Equal to baseline means untouched and yields to the target package; different means a user edit.
 * A layer with no baseline entry is new in the target version, so nothing is carried for it.
 *
 * Indexed with `allLayersDeep`, not `flattenLayers` — the latter descends only into `GROUP`, which left
 * `SELECTION_GROUP` descendants out of both maps and silently replaced their crop and style.
 */
export function carryLayerEditsForUpgrade(
	freshLayers: LayerDto[],
	sourceLayers: LayerDto[],
	baselineLayers: LayerDto[],
	mergeTagState?: Record<string, unknown>
): LayerDto[] {
	if (!freshLayers?.length || !sourceLayers?.length || !baselineLayers?.length) {
		return freshLayers;
	}

	const sourceLayersById = new Map(allLayersDeep(sourceLayers).map(layer => [layer.id, layer]));
	const baselineLayersById = new Map(allLayersDeep(baselineLayers).map(layer => [layer.id, layer]));

	return LayersUtils.mapAllLayers(freshLayers, (freshLayer: LayerDto) => {
		const sourceLayer = sourceLayersById.get(freshLayer?.id);
		const baselineLayer = baselineLayersById.get(freshLayer?.id);
		if (!sourceLayer || !baselineLayer) {
			return freshLayer;
		}

		let carried = freshLayer;

		// Gated on the asset still matching: a crop box drawn against a different image is meaningless.
		if (!isEqual(sourceLayer.cropData, baselineLayer.cropData) && isSameResolvedAsset(freshLayer, sourceLayer, mergeTagState)) {
			carried = { ...carried, cropData: sourceLayer.cropData };
		}

		// Quick Crop stores its final crop as targetCrop rather than cropData. Carry only that
		// user-editable sub-object: resize, quality and the remaining optimization settings are
		// template-owned and must still come from the target package. An explicit clear is carried
		// as undefined so a crop authored by the target version is not resurrected.
		const sourceTargetCrop = sourceLayer.optimizations?.targetCrop;
		const baselineTargetCrop = baselineLayer.optimizations?.targetCrop;
		if (!isEqual(sourceTargetCrop, baselineTargetCrop) && isSameResolvedAsset(freshLayer, sourceLayer, mergeTagState)) {
			carried = {
				...carried,
				optimizations: {
					...freshLayer.optimizations,
					...(sourceTargetCrop ? { provider: 'tasker' as const } : {}),
					targetCrop: sourceTargetCrop
				}
			};

			// A preview provider is merged over the main provider during conversion. Neutralise it
			// when carrying a Tasker crop, while retaining preview quality/resize settings.
			if (sourceTargetCrop && freshLayer.previewOptimizations) {
				carried = {
					...carried,
					previewOptimizations: {
						...freshLayer.previewOptimizations,
						provider: undefined,
						targetCrop: undefined
					}
				};
			}
		}

		// Not asset-gated — a position stays valid whatever asset the layer ends up holding.
		const style = carryChangedKeys(freshLayer.style, sourceLayer.style, baselineLayer.style);
		if (style !== freshLayer.style) {
			carried = { ...carried, style };
		}

		return carried;
	});
}

/**
 * Builds upgraded cart item
 *
 * Rebuilds each item's `itemData` from the target package unit definition and applies
 * carried-over customization values, then updates package/version metadata.
 */
export async function buildCartItemsForInPlaceUpgrade(
	cartItems: CartItem[],
	targetPackage: CreativeUnitPackageDto,
	fullItemsById?: Map<string, CartItem>,
	business?: PublicBusiness,
	options?: BuildCartItemsForInPlaceUpgradeOptions
): Promise<CartItem[]> {
	const sorted = [...cartItems].sort((a, b) => (a.itemData?.position ?? 0) - (b.itemData?.position ?? 0));
	const result: CartItem[] = [];

	for (const item of sorted) {
		const source = fullItemsById?.get(item.id) ?? item;
		const unitId = resolveCreativeUnitIdForTargetPackage(source, targetPackage);
		if (!unitId) {
			console.warn('unit not found on new package', item?.id);
			continue;
		}

		const targetUnit = targetPackage.creativeUnits?.find(u => u.id === unitId);
		if (!targetUnit) {
			continue;
		}

		// The baseline both 3-way carries compare against. Same builder as the target below, so listing
		// mappings sit on both sides or neither.
		const rawBaselineUnit = options?.currentPackage
			? buildUnitForCartItem(options.currentPackage, unitId, source, business, options?.activeGroup)
			: undefined;
		const baselineUnit = rawBaselineUnit ? withResolvedDefaults(rawBaselineUnit) : undefined;
		const baselineCustomizations = baselineUnit ? CustomizationUtils.flattenCustomizations(baselineUnit.customizations || []) : [];
		const baselineLayers = baselineUnit ? allLayersDeep(baselineUnit.layers || []) : [];

		let unit = buildUnitForCartItem(targetPackage, unitId, source, business, options?.activeGroup);
		if (!unit) {
			continue;
		}
		const customizationValues = cartItemCustomizationsToBatchValuesForTargetPackage(
			source.itemData?.customizations,
			targetPackage,
			baselineCustomizations
		);

		unit = applyCarriedCustomizationsToUnit(unit, targetPackage, customizationValues, business, options?.activeGroup);

		const changedSelectIds = getSelectCustomizationIdsWithChangedCarriedValues(source, targetPackage, customizationValues);
		let mergeTagState = MergePropertiesUtils.getMergeableProperties(
			{ ...targetPackage, customizations: unit.customizations },
			business,
			unit,
			options?.activeGroup as any
		);
		for (const selectId of changedSelectIds) {
			unit = clearDownstreamCustomizationValues(unit, selectId, mergeTagState);
			mergeTagState = MergePropertiesUtils.getMergeableProperties(
				{ ...targetPackage, customizations: unit.customizations },
				business,
				unit,
				options?.activeGroup as any
			);
		}

		if (options?.applyDynamicDefaultValues) {
			let resolvedCustomizations = unit.customizations;
			if (changedSelectIds.length) {
				for (const selectId of changedSelectIds) {
					resolvedCustomizations = await options.applyDynamicDefaultValues(
						resolvedCustomizations,
						targetPackage,
						business,
						{ ...unit, customizations: resolvedCustomizations },
						options.activeGroup,
						selectId
					);
					unit = { ...unit, customizations: resolvedCustomizations };
				}
			} else {
				resolvedCustomizations = await options.applyDynamicDefaultValues(
					unit.customizations,
					targetPackage,
					business,
					unit,
					options.activeGroup
				);
				unit = {
					...unit,
					customizations: resolvedCustomizations
				};
			}

			unit = CreativeUnitUtils.updateCreativeUnitWithCustomizationsValues(
				unit,
				CustomizationUtils.flattenCustomizations(unit.customizations)
			);
		}

		// Runs last so the asset guard sees the layer's final asset, not an intermediate one.
		const layers = carryLayerEditsForUpgrade(unit.layers || [], source.itemData?.layers || [], baselineLayers, mergeTagState);

		const itemData = {
			...unit,
			layers,
			// `buildUnitFromListing` mints a random unit id and `resolveCreativeUnitIdForTargetPackage`
			// reads `itemData.id` — keep the item's own id so repeat upgrades stay stable.
			...(source.itemData?.id != null && { id: source.itemData.id }),
			packageId: targetPackage.id,
			creativeUnitId: unitId,
			// An upgrade moves a creative onto a new template version, not onto a new identity, so what
			// the item is called comes with it. Both would otherwise be rebuilt from the target unit:
			// `name` would come back as the new version's unit name — discarding a worksheet row's Name
			// cell, or a name baked from a translated unit name at the locale the item was added in —
			// and `templateCreativeUnit`, which the target unit never carries, would not come back at all.
			...(source.itemData?.name != null && { name: source.itemData.name }),
			...(source.itemData?.templateCreativeUnit != null && {
				templateCreativeUnit: source.itemData.templateCreativeUnit
			}),
			...(source.itemData?.position != null && { position: source.itemData.position }),
			...(source.itemData?.dimensions && { dimensions: source.itemData.dimensions }),
			...(source.itemData?.exportTypes && { exportTypes: source.itemData.exportTypes }),
			...(source.itemData?.group && { group: source.itemData.group })
		};

		const metadata: Record<string, unknown> = {
			...source.metadata,
			packageId: targetPackage.id,
			packageName: targetPackage.name,
			latestPublishedVersionId: targetPackage.latestPublishedVersionId ?? targetPackage.id,
			originalVersionId: targetPackage.originalVersionId ?? targetPackage.id,
			creativeUnitId: unitId
		};
		if (source.metadata?.listingId) {
			metadata['listingId'] = source.metadata.listingId;
			metadata['listingName'] = source.metadata.listingName;
		}

		result.push({
			...source,
			id: source.id,
			name: source.name,
			itemData,
			metadata
		});
	}

	return result;
}
